import { hashArray } from './hash.js';

/**
 *  flyweight - helper function for creating flyweights for specific constructors
 *
 *  @param  {Function} Constructor - a constructor function
 *  @return {Object} - flyweight interface
 */
export const flyweight = (Constructor) => {
    const instances = {};
    const register = (...args) => {
        const key = hashArray(args);
        if (!instances[key]) {
            instances[key] = new Constructor(...args);
        }
        return instances[key];
    };
    return { register };
};

/**
 *  hingleton - helper function for creating singletons
 *
 *  @param {Function} Constructor - constructor of an object to have a singleton of
 *  @param {Array} args - optional arguments to enclose and pass to Constructor on initialization
 *  @return {Object} - object conforming to run-time singleton interface
 *                      yielding a sole instance of Constructor - note that multiple singletons
 *                      of the same Constructor could be created so there is no guarantee that
 *                      this Constructor instance is unique
 */
export const hingleton = (Constructor, ...args) => {
    let instance = null;
    const getInstance = () => {
        if (!instance) {
            instance = new Constructor(...args);
        }
        return instance;
    };
    return { getInstance };
};

/**
 *  mingleton - helper function for creating multitons
 *
 *  @param {Object} hingletons - object containing key -> hingleton pairs
 *  @return {Object} - immutable multiton object of key -> getters calling the hingleton getInstance functions
 */
export const mingleton = (hingletons) => {
    const instances = {};
    Object.keys(hingletons).forEach((key) => {
        Object.defineProperty(instances, key, {
            get() {
                return hingletons[key].getInstance();
            },
        });
    });
    return Object.freeze(instances);
};
